import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import api from '../lib/api';
import { Star, CaretLeft, CaretRight } from '@phosphor-icons/react';

export default function Reviews() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [reviews, setReviews] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [avgRating, setAvgRating] = useState(0);
  const [totalReviews, setTotalReviews] = useState(0);

  useEffect(() => {
    api.get(`/reviews?page=${page}&limit=10`).then(r => {
      setReviews(r.data.reviews);
      setTotalPages(r.data.pages);
      setAvgRating(r.data.avgRating);
      setTotalReviews(r.data.totalReviews);
    }).catch(() => {});
  }, [page]);

  return (
    <div className="pt-20" data-testid="reviews-page">
      <section className="py-24 md:py-32">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="mb-16">
            <p className="text-xs uppercase tracking-[0.2em] text-desti-gold mb-3">Temoignages</p>
            <h1 className="font-heading text-4xl sm:text-5xl text-white">Avis clients</h1>
            <div className="flex items-center gap-4 mt-6">
              <div className="flex gap-1">
                {[1, 2, 3, 4, 5].map(i => (
                  <Star key={i} size={20} weight="fill" className={i <= Math.round(avgRating) ? 'text-desti-gold' : 'text-white/20'} />
                ))}
              </div>
              <span className="text-lg text-white">{avgRating}/5</span>
              <span className="text-sm text-desti-text">({totalReviews} avis)</span>
            </div>
          </motion.div>

          <div className="space-y-4">
            {reviews.map((r, i) => (
              <motion.div key={r.id || i} initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
                className="bg-desti-surface border border-white/5 p-6" data-testid={`review-item-${i}`}>
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-white font-medium">{r.userName || 'Client'}</p>
                    <p className="text-xs text-desti-text mt-1">{new Date(r.createdAt).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}</p>
                  </div>
                  <div className="flex gap-0.5">
                    {[1, 2, 3, 4, 5].map(s => <Star key={s} size={14} weight="fill" className={s <= r.rating ? 'text-desti-gold' : 'text-white/20'} />)}
                  </div>
                </div>
                <p className="text-sm text-white/70 leading-relaxed mt-4">{r.comment}</p>
              </motion.div>
            ))}
          </div>

          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-4 mt-12" data-testid="reviews-pagination">
              <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
                className="p-2 border border-white/10 text-white disabled:opacity-30 hover:border-desti-gold transition-colors">
                <CaretLeft size={16} />
              </button>
              <span className="text-sm text-desti-text">Page {page} / {totalPages}</span>
              <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}
                className="p-2 border border-white/10 text-white disabled:opacity-30 hover:border-desti-gold transition-colors">
                <CaretRight size={16} />
              </button>
            </div>
          )}

          {user && (
            <div className="text-center mt-12">
              <button onClick={() => navigate('/espace-membre/avis')}
                className="bg-desti-gold text-desti-black font-medium text-sm tracking-wide px-8 py-3 hover:bg-desti-gold-hover transition-colors"
                data-testid="leave-review-btn">
                Laisser un avis
              </button>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
